import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  ImagePlus,
  Upload,
  Trash2,
  CheckCircle2,
  ArrowLeft,
  Settings,
} from "lucide-react";
import "./imgupload.scss";
import { uploadImagesApi } from "../../api/api";

/**
 * @returns {JSX.Element}
 */
const Imgupload = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, watch, setValue } = useForm();
  const [isDragging, setIsDragging] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const files = watch("images");
  const fileCount = files ? files.length : 0;

  /**
   * @param {any} data
   */
  const onSubmit = async (data) => {
    // 1. Validación de imágenes
    if (!data.images || data.images.length === 0) {
      alert("Por favor, seleccione al menos una imagen.");
      return;
    }
    // 2. Formateo de datos (PatientName para DICOM)
    const formattedData = {
      ...data,
      patientName:
        `${data.patientLastName}^${data.patientFirstName}`.toUpperCase(),
    };
    // 3. Creación del FormData
    const fd = new FormData();
    // Agregamos los metadatos (excluyendo el campo 'images' del objeto data)
    Object.keys(formattedData).forEach((key) => {
      if (key !== "images") {
        fd.append(key, formattedData[key]);
      }
    });
    // Agregamos las imágenes físicas
    // 'data.images' debe ser un array o FileList
    Array.from(data.images).forEach((file) => {
      fd.append("images", file);
    });
    // 4. Envío usando tu función uploadImagesApi
    try {
      console.log("Iniciando subida...");
      const result = await uploadImagesApi(fd);

      console.log("Éxito:", result);
      alert("Estudio procesado y enviado al PACS con éxito.");
    } catch (error) {
      console.error("Error en el proceso:", error);
      alert("Error: " + error.message);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setValue("images", null);
  };

  const goToMain = () => {
    navigate("/main");
  };

  return (
    <div className="img-upload-container">
      <div className="upload-card">
        <div className="card-header">
          <button type="button" className="btn-back" onClick={goToMain}>
            <ArrowLeft size={18} />
            <span>Dashboard</span>
          </button>
          <button
            type="button"
            className={`btn-settings ${showSettings ? "active" : ""}`}
            onClick={() => setShowSettings(!showSettings)}
            title="Ajustes Avanzados"
          >
            <Settings size={20} />
          </button>
        </div>
        <h1>Subir Imágenes de Estudio</h1>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid">
            <div className="input-group">
              <label>Apellido(s)</label>
              <input
                {...register("patientLastName")}
                type="text"
                placeholder="Apellido"
                defaultValue="APELLIDO"
              />
            </div>
            <div className="input-group">
              <label>Nombre(s)</label>
              <input
                {...register("patientFirstName")}
                type="text"
                placeholder="Nombre"
                defaultValue="NOMBRE"
              />
            </div>
            <div className="input-group">
              <label>ID del Paciente</label>
              <input
                {...register("patientID")}
                type="text"
                placeholder="ID-12345"
                defaultValue="ID123456"
              />
            </div>
            <div className="input-group">
              <label>Modalidad</label>
              <input {...register("modality")} type="text" defaultValue="US" />
            </div>
            <div className="input-group">
              <label>Número de Acceso</label>
              <input
                {...register("accessionNumber")}
                type="text"
                id="accessionNumber"
                placeholder="ACC-123"
                defaultValue={new Date().getTime()}
              />
            </div>
            <div className="input-group">
              <label>Fabricante</label>
              <input
                {...register("manufacturer")}
                type="text"
                defaultValue="MedicareSoft"
              />
            </div>
            <div className="input-group">
              <label>Descripción del Estudio</label>
              <input
                {...register("studyDescription")}
                type="text"
                defaultValue="DESCRIPCION"
              />
            </div>

            {showSettings && (
              <>
                <div className="input-group advanced">
                  <label>Pixel Spacing</label>
                  <input
                    {...register("pixel_spacing")}
                    type="text"
                    defaultValue="0.0972\0.0972"
                  />
                </div>
                <div className="input-group advanced">
                  <label>Imager Pixel Spacing</label>
                  <input
                    {...register("imager_pixel_spacing")}
                    type="text"
                    defaultValue="0.0972\0.0972"
                  />
                </div>
              </>
            )}
          </div>

          <div
            className={`upload-area ${isDragging ? "dragging" : ""}`}
            onDragOver={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={() => setIsDragging(false)}
          >
            {fileCount > 0 && (
              <button
                type="button"
                className="btn-clear"
                onClick={handleClear}
                title="Limpiar archivos"
              >
                <Trash2 size={18} />
                <span>Limpiar</span>
              </button>
            )}
            <div className="upload-icon">
              {fileCount > 0 ? (
                <CheckCircle2 size={48} color="#2da44e" />
              ) : (
                <ImagePlus size={48} />
              )}
            </div>
            <span id="fileCount">
              {fileCount > 0
                ? `${fileCount} ${fileCount === 1 ? "archivo seleccionado" : "archivos seleccionados"}`
                : "Ningún archivo seleccionado"}
            </span>
            <p style={{ fontSize: "0.85rem", color: "#57606a" }}>
              Haz clic o arrastra para subir imágenes JPEG
            </p>
            <input
              type="file"
              {...register("images")}
              id="fileInput"
              multiple
              accept="image/jpeg,image/jpg"
            />
          </div>

          <button type="submit" className="btn-submit">
            <span>Adjuntar Imagenes</span>
            <Upload size={20} />
          </button>

          <div id="status" className="status"></div>
        </form>
      </div>
    </div>
  );
};

export default Imgupload;
